/**
 * Accountant Analysis Tools API Service
 *
 * TypeScript client for the accountant robustness endpoints
 * (POST /api/v1/accountant-analysis/{reportId}/monte-carlo and /sensitivity).
 * These run the existing deterministic engines against the claim in db.drafts
 * and answer the two "defend the number" questions: how robust is the claim
 * (confidence interval) and which driver moves it most (sensitivity sweep).
 *
 * In-house only: the endpoints are org-scoped via the Clerk session
 * (get_current_user). Uses the shared `api` utility for auth + error handling.
 *
 * NOTE: the backend reads its inputs from query params (not a JSON body), so
 * these helpers encode the knobs into the query string and POST an empty body.
 */

import { api } from '../utils/api';

const BASE = '/api/v1/accountant-analysis';

export type SensitivityParameter = 'payroll' | 'revenue' | 'variable_expenses';

export interface MonteCarloStats {
  mean: number;
  median: number;
  std_dev: number;
  ci_95_low: number;
  ci_95_high: number;
  min: number;
  max: number;
}

export interface MonteCarloResult {
  base_claim: number;
  iterations: number;
  noise_pct: number;
  results: MonteCarloStats;
  /** Up to 100 simulated claim values, for the distribution histogram. */
  distribution_sample: number[];
  report_id: string;
}

export interface SensitivityPoint {
  pct: number;
  claim: number;
  change_pct: number;
}

export interface SensitivityResult {
  base_claim: number;
  parameter: string;
  results: SensitivityPoint[];
  range: { min: number; max: number; spread: number };
  report_id: string;
}

/** Monte Carlo confidence interval around the claim (≥1,000 runs, ±noise Gaussian). */
export async function runMonteCarlo(
  reportId: string,
  opts?: { iterations?: number; noisePct?: number; seed?: number },
): Promise<MonteCarloResult> {
  const qs = new URLSearchParams({
    iterations: String(opts?.iterations ?? 2000),
    noise_pct: String(opts?.noisePct ?? 0.15),
    seed: String(opts?.seed ?? 42),
  });
  return api.post<MonteCarloResult>(`${BASE}/${reportId}/monte-carlo?${qs.toString()}`, {});
}

/** Sensitivity sweep: how the claim moves as one driver varies min_pct → max_pct. */
export async function runSensitivity(
  reportId: string,
  opts?: { parameter?: SensitivityParameter; minPct?: number; maxPct?: number; stepPct?: number },
): Promise<SensitivityResult> {
  const qs = new URLSearchParams({
    parameter: opts?.parameter ?? 'payroll',
    min_pct: String(opts?.minPct ?? 80),
    max_pct: String(opts?.maxPct ?? 120),
    step_pct: String(opts?.stepPct ?? 5),
  });
  return api.post<SensitivityResult>(`${BASE}/${reportId}/sensitivity?${qs.toString()}`, {});
}
